import {
  CurrentRepository,
  RepositoryFragment,
  type RepositoryPickerCurrentRepoQuery,
  type RepositoryPickerRepository$data as Repository,
  type RepositoryPickerRepository$key,
  type RepositoryPickerTopRepositoriesQuery,
  type RepositoryPickerRepositoryIssueTemplates$key,
  type RepositoryPickerRepositoryIssueTemplates$data as Templates,
  RepositoryIssueTemplatesFragment,
} from '@github-ui/item-picker/repository'
import {
  type AssigneePickerAssignee$data as Assignee,
  type AssigneePickerAssignee$key,
  AssigneeFragment,
} from '@github-ui/item-picker/assignee'
import {
  type LabelPickerLabel$data as Label,
  type LabelPickerLabel$key,
  LabelFragment,
} from '@github-ui/item-picker/label'
import type {MilestonePickerMilestone$data as Milestone} from '@github-ui/item-picker/milestone'
import {
  type ProjectPickerProject$data as Project,
  type ProjectPickerProject$key,
  ProjectPickerProjectFragment,
} from '@github-ui/item-picker/project'
import {clearSessionStorage, useSessionStorage} from '@github-ui/use-safe-storage'
import {Box} from '@primer/react'
import {type RefObject, Suspense, useCallback, useEffect, useImperativeHandle, useState} from 'react'
import {type PreloadedQuery, readInlineData, usePreloadedQuery} from 'react-relay'

import {VALUES} from './constants'
import {CreateIssueForm, type CreateIssueCallbackProps} from './CreateIssueForm'
import {TemplatePickerButton} from './TemplatePickerButton'
import {RepositoryAndTemplatePickerDialog} from './RepositoryAndTemplatePickerDialog'

import {
  instanceOfIssueTemplateData,
  instanceOfIssueFormData,
  getIssueTemplate,
  getBlankIssue,
  BLANK_ISSUE,
  BLANK_ISSUE_ID,
  instanceOfBlankIssueData,
  instanceOfContactLinkData,
} from './utils/model'
import {TemplateListPane} from './TemplateListPane'
import {CreateIssueFooter, type IssueCreatePayload, storageKeys} from '.'
import {TemplateListLoading} from './TemplateList'
import {DisplayMode} from './utils/display-mode'
import {type IssueCreateUrlParams, type IssueCreateValueTypes, hasAnyInitialValues} from './utils/template-args'
import {useIssueCreateContext} from './contexts/IssueCreateContext'
import {constructTemplateUrlParamsIfAppropriate, setTemplateUrlParamIfAppropriate} from './utils/urls'
import {IssueTypeFragment, type IssueType, type IssueTypePickerIssueType$key} from '@github-ui/item-picker/issue-type'
import type {SafeOptionConfig} from './utils/option-config'

export type CreateIssueSharedProps = {
  topReposQueryRef?: PreloadedQuery<RepositoryPickerTopRepositoriesQuery>
  preselectedTemplate?: IssueCreatePayload
  optionConfig: SafeOptionConfig
} & CreateIssueCallbackProps

type CreateIssueInternalSharedProps = {
  insidePortal: boolean
  createIssueUrlParamsRef?: RefObject<IssueCreateUrlParams>
  preselectedRepository?: Repository
  templates?: Templates | null
}

type CreateIssueProps = CreateIssueSharedProps & CreateIssueInternalSharedProps
type CreateIssueWithPreselectedRepoProps = CreateIssueProps & {
  currentRepoQueryRef: PreloadedQuery<RepositoryPickerCurrentRepoQuery>
}

export const CreateIssueWithPreselectedRepo = ({
  currentRepoQueryRef,
  optionConfig,
  ...createIssueProps
}: CreateIssueWithPreselectedRepoProps): JSX.Element => {
  const preloadedData = usePreloadedQuery<RepositoryPickerCurrentRepoQuery>(CurrentRepository, currentRepoQueryRef)
  const preselectedRepository = readInlineData<RepositoryPickerRepository$key>(
    RepositoryFragment,
    preloadedData.repository,
  )

  const templates = readInlineData<RepositoryPickerRepositoryIssueTemplates$key>(
    RepositoryIssueTemplatesFragment,
    preloadedData.repository,
  )

  const templateFileName = optionConfig.issueCreateArguments?.templateFileName
  const selectedTemplate =
    templates?.issueTemplates?.find(template => template.filename === templateFileName) ||
    templates?.issueForms?.find(form => form.filename === templateFileName)

  const preselectedTemplateData =
    (selectedTemplate && getIssueTemplate(selectedTemplate)) ||
    (templateFileName === BLANK_ISSUE && getBlankIssue()) ||
    (hasAnyInitialValues(optionConfig.issueCreateArguments?.initialValues) && getBlankIssue()) ||
    undefined

  return (
    <CreateIssue
      preselectedRepository={preselectedRepository ?? undefined}
      templates={templates}
      preselectedTemplate={preselectedTemplateData}
      optionConfig={optionConfig}
      {...createIssueProps}
    />
  )
}

export const CreateIssue = ({
  topReposQueryRef,
  preselectedRepository,
  preselectedTemplate,
  templates,
  createIssueUrlParamsRef,
  optionConfig,
  ...props
}: CreateIssueProps) => {
  const {
    issueTitle,
    issueRepoId,
    issueTemplateId,
    issueLabels,
    issueAssignees,
    issueBody,
    issueMilestone,
    issueProjects,
    issueIssueType,
  } = VALUES.localStorageKeys
  const {storageKeyPrefix} = useIssueCreateContext()
  const [repository, setRepository] = useState<Repository | undefined>(preselectedRepository)
  const [repositoryId, setRepositoryId] = useSessionStorage<string | undefined>(
    issueRepoId(storageKeyPrefix),
    undefined, // this is needed to be undefined for template content clean to work when changing repos
  )

  const [template, setTemplate] = useState<IssueCreatePayload | undefined>(undefined)
  const [templateId, setTemplateId] = useSessionStorage<string | undefined>(
    issueTemplateId(storageKeyPrefix),
    undefined,
  )

  const [title, setTitle] = useSessionStorage<string>(issueTitle(storageKeyPrefix), '')
  const [body, setBody] = useSessionStorage<string>(issueBody(storageKeyPrefix), '')

  const [labels, setLabels] = useSessionStorage<Label[]>(issueLabels(storageKeyPrefix), [])
  const [assignees, setAssignees] = useSessionStorage<Assignee[]>(issueAssignees(storageKeyPrefix), [])
  const [projects, setProjects] = useSessionStorage<Project[]>(issueProjects(storageKeyPrefix), [])
  const [milestone, setMilestone] = useSessionStorage<Milestone | null>(issueMilestone(storageKeyPrefix), null)
  const [issueType, setIssueType] = useSessionStorage<IssueType | null>(issueIssueType(storageKeyPrefix), null)

  const {displayMode, setDisplayMode, setCreateMoreCreatedPath} = useIssueCreateContext()

  // On first render, if we have a pre-selected repository and template, we should set these.
  useEffect(() => {
    if (preselectedRepository) {
      onRepositorySelected(preselectedRepository)

      if (preselectedTemplate) {
        // On first render - we want to take into consideration the default parameters as the default state values if no template is currently set.
        // Meaning, we take precedence of the default parameters over the template's default values, unless there are already values in sessionStorage
        // or we are changing template (this logic is handled inside the function).
        onTemplateSelected(preselectedTemplate, optionConfig.issueCreateArguments?.initialValues)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearMetadata = useCallback(() => {
    setAssignees([])
    setLabels([])
    setProjects([])
    setMilestone(null)
  }, [setAssignees, setLabels, setMilestone, setProjects])

  const clearSessionData = useCallback(() => {
    clearSessionStorage(storageKeys(storageKeyPrefix))
  }, [storageKeyPrefix])

  const constructUrlSearchParams = useCallback(
    () =>
      constructTemplateUrlParamsIfAppropriate({
        repository,
        template,
        preselectedRepository,
        title,
        body,
        assignees,
        labels,
        milestone: milestone ?? undefined,
        type: issueType ?? undefined,
        projects,
        displayMode,
      }),
    [
      repository,
      template,
      preselectedRepository,
      title,
      body,
      assignees,
      labels,
      milestone,
      issueType,
      projects,
      displayMode,
    ],
  )

  useImperativeHandle(
    createIssueUrlParamsRef,
    () => ({
      generateUrlParams: () => constructUrlSearchParams(),
    }),
    [constructUrlSearchParams],
  )

  const onRepositorySelected = useCallback(
    (selectedRepository: Repository | undefined) => {
      if (selectedRepository) {
        if (repositoryId !== selectedRepository.id) {
          // We are only clearing metadata because we want to persist title & body across repo changes incase we are selecting blank issues
          clearMetadata()
        }

        setRepository(selectedRepository)
        setRepositoryId(selectedRepository.id)
        setCreateMoreCreatedPath({
          owner: selectedRepository.owner.login,
          repo: selectedRepository.name,
          number: undefined,
        })
      }
    },
    [clearMetadata, repositoryId, setCreateMoreCreatedPath, setRepositoryId],
  )

  const onTemplatePickerClick = useCallback(() => {
    setDisplayMode(DisplayMode.TemplatePicker)
  }, [setDisplayMode])

  const onTemplateSelected = useCallback(
    (selectedTemplate: IssueCreatePayload, enforcedInitialValues?: IssueCreateValueTypes) => {
      // Contact links will redirect to a new page, so we don't want to set the template
      if (instanceOfContactLinkData(selectedTemplate.data)) {
        return
      }

      const changedTemplates = templateId !== selectedTemplate.data?.__id
      setTemplate(selectedTemplate)
      setTemplateId(selectedTemplate.data?.__id)

      if (changedTemplates) {
        setTemplateUrlParamIfAppropriate({
          insidePortal: props.insidePortal,
          template: selectedTemplate,
          repository,
          preselectedRepository,
        })

        const previouslyWasBlankIssue = templateId === BLANK_ISSUE_ID
        clearMetadata()
        const newTitle =
          enforcedInitialValues?.title ??
          (selectedTemplate.data.title ?? '') +
            (previouslyWasBlankIssue && title && title.length > 0 ? ` ${title}` : '')
        setTitle(newTitle)

        if (instanceOfIssueTemplateData(selectedTemplate.data) || instanceOfBlankIssueData(selectedTemplate.data)) {
          const newBody =
            enforcedInitialValues?.body ??
            (!previouslyWasBlankIssue || body?.length === 0 ? '' : `${body}\n\n---\n`) + selectedTemplate.data.body
          setBody(newBody)
        } else {
          setBody('')
        }

        const templateLabels =
          enforcedInitialValues?.labels ??
          (selectedTemplate.data.labels?.edges?.flatMap(e =>
            e?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, e?.node)] : [],
          ) ||
            [])
        setLabels(templateLabels)

        const templateAssignees =
          enforcedInitialValues?.assignees ??
          ((selectedTemplate.data.assignees?.edges || []).flatMap(e =>
            e?.node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, e?.node)] : [],
          ) ||
            [])
        setAssignees(templateAssignees)

        if (enforcedInitialValues?.projects) {
          setProjects(enforcedInitialValues.projects)
        } else if (instanceOfIssueFormData(selectedTemplate.data)) {
          const templateProjects =
            selectedTemplate.data.projects?.edges?.flatMap(e =>
              e?.node ? [readInlineData<ProjectPickerProject$key>(ProjectPickerProjectFragment, e?.node)] : [],
            ) || []
          setProjects(templateProjects)
        }

        if (enforcedInitialValues?.milestone) {
          setMilestone(enforcedInitialValues.milestone)
        }

        let templateType = enforcedInitialValues?.type || null
        if (
          !templateType &&
          (instanceOfIssueFormData(selectedTemplate.data) || instanceOfIssueTemplateData(selectedTemplate.data))
        ) {
          templateType = readInlineData<IssueTypePickerIssueType$key>(IssueTypeFragment, selectedTemplate.data.type)
        }
        setIssueType(templateType)
      }

      setDisplayMode(DisplayMode.IssueCreation)
    },
    [
      templateId,
      setTemplateId,
      setDisplayMode,
      props.insidePortal,
      repository,
      preselectedRepository,
      clearMetadata,
      title,
      setTitle,
      setLabels,
      setAssignees,
      body,
      setBody,
      setProjects,
      setMilestone,
      setIssueType,
    ],
  )

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2}} tabIndex={-1}>
      {displayMode === DisplayMode.IssueCreation && (
        <>
          {topReposQueryRef && (
            <Box sx={{width: 'content'}} data-testid="template-picker-button">
              <TemplatePickerButton repository={repository} template={template} onClick={onTemplatePickerClick} />
            </Box>
          )}
          {repository && (
            <>
              <CreateIssueForm
                {...props}
                selectedTemplate={template}
                repository={repository}
                title={title}
                setTitle={setTitle}
                body={body}
                setBody={setBody}
                labels={labels}
                setLabels={setLabels}
                assignees={assignees}
                setAssignees={setAssignees}
                projects={projects}
                setProjects={setProjects}
                milestone={milestone}
                setMilestone={setMilestone}
                issueType={issueType}
                setIssueType={setIssueType}
                clearOnCreate={clearSessionData}
                focusTitleInput={!topReposQueryRef}
                optionConfig={optionConfig}
              />
              {/* The dialog renders the footer separately */}
              {!props.insidePortal && <CreateIssueFooter sx={{mt: 2}} onClose={props.onCancel} />}
            </>
          )}
        </>
      )}
      {optionConfig.showRepositoryPicker && displayMode === DisplayMode.TemplatePicker && topReposQueryRef && (
        <RepositoryAndTemplatePickerDialog
          repository={repository}
          setRepository={onRepositorySelected}
          topReposQueryRef={topReposQueryRef}
          organization={optionConfig.scopedOrganization}
          onTemplateSelected={onTemplateSelected}
          variant="large"
        />
      )}
      {!optionConfig.showRepositoryPicker && displayMode === DisplayMode.TemplatePicker && repository && (
        <Suspense fallback={<TemplateListLoading />}>
          {repository && (
            <Box data-hpc>
              <TemplateListPane
                variant="full"
                repository={repository}
                templates={templates}
                onTemplateSelected={onTemplateSelected}
              />
            </Box>
          )}
        </Suspense>
      )}
    </Box>
  )
}

try{ CreateIssueWithPreselectedRepo.displayName ||= 'CreateIssueWithPreselectedRepo' } catch {}
try{ CreateIssue.displayName ||= 'CreateIssue' } catch {}